import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

import PanelTemplate from "../../../templates/Panel";
import PageHeader from "../../../components/PageHeader";
import Box from "../../../components/Box";
import BoxKpi from "../../../components/dashboard/BoxKpi";
import Button from "../../../components/Button";
import Icon from "../../../components/Icon";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import { Loading } from "../../../components/Loading";
import Pagination from "../../../components/Pagination";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { hasPermission } from "../../../services/auth";
import { convertIntToMoney } from "../../../helpers/conversions";

import OpenFinanceButton from "../OpenFinance";
import ModalImport from "./ModalImport";
import ModalMatch from "./ModalMatch";

const STATUS_TABS = [
    { label: 'Pendentes', value: 'pending', countKey: 'pending' },
    { label: 'Conciliados', value: 'matched', countKey: 'matched' },
    { label: 'Ignorados', value: 'ignored', countKey: 'ignored' },
];

const normalizePage = page => {
    const parsedPage = parseInt(page, 10);
    return Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;
};

const parseFilters = search => {
    const params = new URLSearchParams(search);

    return {
        page: normalizePage(params.get('page')),
        status: params.get('status') || 'pending',
        accountId: params.get('account') || '',
        search: params.get('search') || '',
    };
};

const buildSearch = filters => {
    const params = new URLSearchParams();

    if (filters.page > 1) params.set('page', filters.page);
    if (filters.status !== 'pending') params.set('status', filters.status);
    if (filters.accountId) params.set('account', filters.accountId);
    if (filters.search.trim() !== '') params.set('search', filters.search.trim());

    return params.toString();
};

// Conciliação bancária (BDM-1047): o extrato de um lado, os títulos do outro.
export const FinanceReconciliation = () => {
    const [loading, setLoading] = useState(true);

    const [bankAccounts, setBankAccounts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [costCenters, setCostCenters] = useState([]);

    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState(null);

    const [showImport, setShowImport] = useState(false);
    const [transactionToMatch, setTransactionToMatch] = useState(null);
    const [actingId, setActingId] = useState(null);
    const [searchDraft, setSearchDraft] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const filters = useMemo(() => parseFilters(location.search), [location.search]);

    const canManage = hasPermission('manage_finance');

    // Sem conta na URL vale a primeira conta ativa: a tela só faz sentido
    // dentro de uma conta, e escolher aqui evita uma navegação extra antes do
    // primeiro carregamento.
    const accountId = filters.accountId || bankAccounts[0]?.id || '';
    const account = bankAccounts.find(bankAccount => bankAccount.id === accountId) ?? null;

    useEffect(() => {
        api.get(`/finance-bank-account`)
            .then(res => setBankAccounts(res.data.resources.filter(bankAccount => bankAccount.active)))
            .catch(renderError)
            .then(() => setLoading(false));
        api.get(`/finance-category`).then(res => setCategories(res.data.resources)).catch(() => {});
        api.get(`/finance-cost-center`).then(res => setCostCenters(res.data.resources)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!accountId) return;

        getTransactions();
        getSummary();
    }, [location.search, accountId]);

    useEffect(() => {
        setSearchDraft(filters.search);
    }, [filters.search]);

    const updateUrl = (changes, resetPage = true) => {
        const nextFilters = {
            ...filters,
            accountId,
            ...changes,
            page: resetPage ? 1 : (changes.page || filters.page),
        };
        const search = buildSearch(nextFilters);

        navigate({ pathname: location.pathname, search: search ? `?${search}` : '' });
    };

    const getTransactions = () => {
        setLoading(true);

        const params = new URLSearchParams();
        params.set('finance_bank_account_id', accountId);
        params.set('status[]', filters.status);
        params.set('page', filters.page);
        if (filters.search.trim() !== '') params.set('search', filters.search.trim());
        if (filters.status === 'pending') params.set('with_suggestions', 1);

        api.get(`/finance-reconciliation/transaction/paginate?${params.toString()}`).then(res => {
            setTransactions(res.data.resources.data);
            setCurrentPage(res.data.resources.current_page);
            setLastPage(res.data.resources.last_page);
            setPerPage(res.data.resources.per_page);
            setTotalResults(res.data.resources.total);
            setHasNextPage(res.data.resources.next_page_url !== null);
            setHasPrevPage(res.data.resources.prev_page_url !== null);
        }).catch(renderError).then(() => setLoading(false));
    };

    const getSummary = () => {
        api.get(`/finance-reconciliation/summary?finance_bank_account_id=${accountId}`)
            .then(res => setSummary(res.data.resource))
            .catch(() => setSummary(null));
    };

    const updateBankAccount = updated => {
        setBankAccounts(current => current.map(bankAccount => bankAccount.id === updated.id ? updated : bankAccount));
    };

    const refresh = () => {
        getTransactions();
        getSummary();
    };

    const runAction = (transaction, request, successMessage) => {
        setActingId(transaction.id);

        request.then(() => {
            toast.success(successMessage);
            refresh();
        }).catch(renderError).then(() => setActingId(null));
    };

    const reconcileSuggestion = (transaction, suggestion) => {
        runAction(
            transaction,
            api.post(`/finance-reconciliation/transaction/${transaction.id}/reconcile`, {
                items: [{ finance_entry_id: suggestion.entry.id, amount: transaction.amount }],
            }),
            'Lançamento conciliado'
        );
    };

    const undo = transaction => {
        runAction(
            transaction,
            api.post(`/finance-reconciliation/transaction/${transaction.id}/undo`),
            'Conciliação desfeita'
        );
    };

    const ignore = transaction => {
        runAction(
            transaction,
            api.patch(`/finance-reconciliation/transaction/${transaction.id}/ignore`),
            'Lançamento ignorado'
        );
    };

    const restore = transaction => {
        runAction(
            transaction,
            api.patch(`/finance-reconciliation/transaction/${transaction.id}/restore`),
            'Lançamento devolvido para a fila'
        );
    };

    const renderStatusLine = transaction => {
        if (transaction.status === 'matched') {
            const target = transaction.transfer
                ? `Transferência · ${transaction.transfer.to_account?.name ?? 'outra conta'}`
                : (transaction.settlements ?? [])
                    .map(settlement => settlement.entry?.description)
                    .filter(Boolean)
                    .join(' · ');

            return (
                <span className={`row-match`}>
                    <Icon name={`check`} size={14} />
                    {target || 'Conciliado'}
                </span>
            );
        }

        if (transaction.status === 'ignored') {
            return <span className={`row-hint`}>Fora da conciliação</span>;
        }

        const suggestion = (transaction.suggestions ?? [])[0];

        if (!suggestion) {
            return <span className={`row-hint`}>Sem título correspondente</span>;
        }

        return (
            <span className={`row-suggestion`}>
                <span className={`suggestion-chip`}>Sugestão</span>
                <span className={`suggestion-entry`}>{suggestion.entry.description}</span>
                <span className={`suggestion-meta`}>
                    vence {moment(suggestion.entry.due_date).format('DD/MM/YYYY')}
                    {!suggestion.exact_amount && ` · em aberto ${convertIntToMoney(suggestion.remaining_amount, true)}`}
                </span>
            </span>
        );
    };

    const renderActions = transaction => {
        const suggestion = (transaction.suggestions ?? [])[0];
        const acting = actingId === transaction.id;

        if (transaction.status === 'matched') {
            return <Button type={`secondary`} size={`small`} text={`Desfazer`} action={() => undo(transaction)} loading={acting} />;
        }

        if (transaction.status === 'ignored') {
            return <Button type={`secondary`} size={`small`} text={`Voltar para a fila`} action={() => restore(transaction)} loading={acting} />;
        }

        return (
            <>
                {suggestion?.exact_amount &&
                    <Button type={`primary`} size={`small`} text={`Conciliar`} action={() => reconcileSuggestion(transaction, suggestion)} loading={acting} />
                }
                <Button
                    type={`secondary`}
                    size={`small`}
                    text={suggestion?.exact_amount ? `Outro título` : `Escolher título`}
                    action={() => setTransactionToMatch(transaction)}
                />
                {/* Ignorar é saída de exceção: fica discreta para não disputar
                    a atenção com a ação que resolve o lançamento. */}
                <button className={`row-ignore`} onClick={() => ignore(transaction)} disabled={acting}>
                    Ignorar
                </button>
            </>
        );
    };

    const accountOptions = bankAccounts.map(bankAccount => ({ label: bankAccount.name, value: bankAccount.id }));

    const difference = summary && summary.statement_balance !== null && summary.system_balance !== null
        ? summary.statement_balance - summary.system_balance
        : null;

    const hasAccounts = bankAccounts.length > 0;
    const nothingImported = hasAccounts && summary
        && summary.pending === 0 && summary.matched === 0 && summary.ignored === 0;

    return (
        <>
            <ModalImport
                show={showImport}
                close={() => setShowImport(false)}
                bankAccount={account}
                onImported={refresh}
            />
            <ModalMatch
                show={transactionToMatch !== null}
                close={() => setTransactionToMatch(null)}
                transaction={transactionToMatch}
                categories={categories}
                costCenters={costCenters}
                bankAccounts={bankAccounts}
                onDone={refresh}
            />

            <PanelTemplate id="finance-reconciliation">
                <PageHeader
                    left={
                        <>
                            <div className={`reconciliation-account-select`}>
                                <InputSelect
                                    label={`Conta`}
                                    options={accountOptions}
                                    value={accountId}
                                    change={value => updateUrl({ accountId: value })}
                                />
                            </div>
                            <div className={`reconciliation-search`}>
                                <Input
                                    label={`Buscar no histórico`}
                                    value={searchDraft}
                                    change={setSearchDraft}
                                    action={() => updateUrl({ search: searchDraft })}
                                    onBlur={() => searchDraft !== filters.search && updateUrl({ search: searchDraft })}
                                />
                            </div>
                        </>
                    }
                    right={
                        <>
                            <OpenFinanceButton bankAccount={account} onChange={updateBankAccount} />
                            {canManage && hasAccounts &&
                                <Button type={`primary`} size={`small`} svg={`import`} text={`Importar extrato`} action={() => setShowImport(true)} />
                            }
                        </>
                    }
                />

                {!hasAccounts && !loading &&
                    <Box>
                        <div className={`reconciliation-empty`}>
                            <Icon name={`money`} size={28} />
                            <strong>Nenhuma conta bancária cadastrada</strong>
                            <span>Cadastre a conta em <strong>Gestão › Cadastros e ajustes</strong> para começar a conciliar.</span>
                            {canManage &&
                                <Button type={`secondary`} size={`small`} text={`Cadastrar conta`} action={() => navigate('/finance/settings')} />
                            }
                        </div>
                    </Box>
                }

                {hasAccounts && summary &&
                    <div className={`reconciliation-kpis`}>
                        <BoxKpi
                            icon={`inbox`}
                            accent={summary.pending > 0 ? 'coral' : 'brand'}
                            label={`A conciliar`}
                            value={summary.pending}
                            hint={summary.pending > 0 ? `${convertIntToMoney(summary.pending_amount ?? 0, true)} em lançamentos` : 'Extrato em dia'}
                        />
                        <BoxKpi
                            icon={`check`}
                            accent={`teal`}
                            label={`Conciliados`}
                            value={summary.matched}
                            hint={summary.ignored > 0 ? `${summary.ignored} ignorado(s)` : null}
                        />
                        <BoxKpi
                            icon={`money`}
                            label={`Saldo do extrato`}
                            money={summary.statement_balance ?? 0}
                            hint={summary.statement_balance_at
                                ? `Banco em ${moment(summary.statement_balance_at).format('DD/MM/YYYY')}`
                                : 'Sem saldo informado no arquivo'}
                        />
                        <BoxKpi
                            icon={`calculator`}
                            accent={`cobalt`}
                            label={`Saldo no sistema`}
                            money={summary.system_balance ?? 0}
                            // A diferença é esperada enquanto houver lançamento
                            // pendente — dizer isso aqui evita ler o número
                            // como erro de saldo.
                            hint={difference === null
                                ? null
                                : difference === 0
                                    ? 'Igual ao extrato'
                                    : `${convertIntToMoney(difference, true)} de diferença${summary.pending > 0 ? ' — há lançamentos a conciliar' : ''}`}
                        />
                    </div>
                }

                {hasAccounts &&
                    <Box className={`no-padding with-pagination`}>
                        <div className={`reconciliation-tabs`}>
                            {STATUS_TABS.map(tab => (
                                <button
                                    key={tab.value}
                                    className={filters.status === tab.value ? 'active' : ''}
                                    onClick={() => updateUrl({ status: tab.value })}
                                >
                                    {tab.label}
                                    {summary && <span className={`tab-count`}>{summary[tab.countKey]}</span>}
                                </button>
                            ))}
                        </div>

                        <Loading show={loading} />

                        {!loading && transactions.length === 0 &&
                            <div className={`reconciliation-empty`}>
                                <Icon name={nothingImported ? `import` : `check`} size={28} />
                                <strong>
                                    {nothingImported
                                        ? 'Nenhum extrato importado nesta conta'
                                        : filters.status === 'pending'
                                            ? 'Tudo conciliado por aqui'
                                            : 'Nenhum lançamento neste filtro'}
                                </strong>
                                {nothingImported && account?.open_finance_status === 'active' &&
                                    <span>A conta está conectada ao banco: os lançamentos chegam na próxima sincronização.</span>
                                }
                                {nothingImported && account?.open_finance_status !== 'active' &&
                                    <span>Baixe o OFX no internet banking e use <strong>Importar extrato</strong>.</span>
                                }
                            </div>
                        }

                        {!loading && transactions.map(transaction => (
                            <div key={transaction.id} className={`reconciliation-row status-${transaction.status}`}>
                                <div className={`row-date`}>
                                    <strong>{moment(transaction.posted_at).format('DD/MM')}</strong>
                                    <small>{moment(transaction.posted_at).format('YYYY')}</small>
                                </div>

                                <div className={`row-info`}>
                                    <strong className={`row-memo`}>{transaction.memo || 'Lançamento sem descrição'}</strong>
                                    {transaction.removed_by_bank_at &&
                                        <span className={`row-removed`}>
                                            <Icon name={`alert`} size={14} />
                                            O banco retirou este lançamento do extrato. Confira e desfaça a conciliação se ele não aconteceu.
                                        </span>
                                    }
                                    {renderStatusLine(transaction)}
                                </div>

                                <div className={`row-amount ${transaction.type}`}>
                                    {transaction.type === 'credit' ? '+' : '−'} {convertIntToMoney(transaction.amount, true)}
                                </div>

                                {canManage &&
                                    <div className={`row-actions`}>
                                        {renderActions(transaction)}
                                    </div>
                                }
                            </div>
                        ))}
                    </Box>
                }

                {!loading && hasAccounts && transactions.length > 0 &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={page => updateUrl({ page }, false)}
                    />
                }
            </PanelTemplate>
        </>
    );
};

export default FinanceReconciliation;
